import type { UpdateCheckStatus } from '~/hooks/useElectronUpdater'

import type { DiagnosticsSnapshot, HealthLevel } from './useDiagnosticsSnapshot'

/**
 * The exact slice of the snapshot the health functions read. Declaring it
 * explicitly (rather than taking the full DiagnosticsSnapshot) lets callers
 * like useOverallHealth build just these fields and have the compiler verify
 * the shape — instead of an `as` cast that would silently pass `undefined` if
 * a health function later reached for a field outside this set.
 */
export type HealthInput = Pick<DiagnosticsSnapshot, 'network' | 'sip'> & {
  websocket: Pick<DiagnosticsSnapshot['websocket'], 'connectionStatus'>
}

export interface HealthRow {
  level: HealthLevel
  /** A `statusPanel.*` translation key for the human-readable state label. */
  labelKey: string
  /** Interpolation values for `labelKey`. */
  labelValues?: Record<string, string>
  /**
   * Optional translation key for a short, actionable troubleshooting
   * suggestion, shown under the row when it isn't healthy. Only set for states
   * with a confident, specific remedy — left undefined otherwise so we never
   * show vague or misleading advice.
   */
  fixKey?: string
}

// Ranking for the "worst-of" overall roll-up. `idle` and `unknown` sit at or
// below `healthy` so an informational state (e.g. the webphone intentionally
// not connected) never makes the overall status look like a problem.
const LEVEL_RANK: Record<HealthLevel, number> = {
  idle: -1,
  healthy: 0,
  unknown: 0,
  degraded: 2,
  down: 3,
}

export const HEALTH_COLOR: Record<HealthLevel, string> = {
  healthy: 'green.solid',
  degraded: 'orange.solid',
  down: 'red.solid',
  idle: 'gray.solid',
  unknown: 'gray.solid',
}

// `statusPanel.*` key for the overall badge label, by level.
export const OVERALL_LABEL_KEY: Record<HealthLevel, string> = {
  healthy: 'statusPanel.overallHealthy',
  degraded: 'statusPanel.overallDegraded',
  down: 'statusPanel.overallDown',
  idle: 'statusPanel.overallIdle',
  unknown: 'statusPanel.overallUnknown',
}

export const OVERALL_LABEL_FALLBACK: Record<HealthLevel, string> = {
  healthy: 'Healthy',
  degraded: 'Degraded',
  down: 'Down',
  idle: 'Idle',
  unknown: 'Unknown',
}

/** States that are still working towards a result. */
export const IN_PROGRESS_LABEL_KEYS: ReadonlySet<string> = new Set([
  'statusPanel.stateCheckingForUpdates',
  'statusPanel.stateUpdateDownloading',
  'statusPanel.stateUpdateDownloadingVersion',
  'statusPanel.stateConnecting',
])

export const BADGE_PALETTE: Record<HealthLevel, string> = {
  healthy: 'green',
  idle: 'gray',
  unknown: 'gray',
  degraded: 'orange',
  down: 'red',
}

export function networkHealth(snap: HealthInput): HealthRow {
  return snap.network.online
    ? { level: 'healthy', labelKey: 'statusPanel.stateOnline' }
    : {
        level: 'down',
        labelKey: 'statusPanel.stateOffline',
        fixKey: 'statusPanel.fixNetworkOffline',
      }
}

export function phoneServiceHealth(snap: HealthInput): HealthRow {
  const { registrationState, transportState, registrationError } = snap.sip

  if (registrationState === 'Registered' && transportState === 'CONNECTED') {
    return { level: 'healthy', labelKey: 'statusPanel.stateRegisteredReady' }
  }
  // Not connected — either never started (webphone off / not in use) or torn
  // down. This is a normal state for users who don't run the webphone, so we
  // surface it neutrally rather than as a problem — UNLESS a registration error
  // is present, in which case we let it fall through to the degraded check
  // below (a failed registration can land in 'Unregistered' with an error, and
  // that's a real problem, not idle).
  if (
    !registrationError &&
    (transportState === 'INITIAL' ||
      transportState === 'DISCONNECTED' ||
      registrationState === 'Initial' ||
      registrationState === 'Unregistered' ||
      registrationState === 'Terminated')
  ) {
    return { level: 'idle', labelKey: 'statusPanel.stateDisconnected' }
  }
  // A registration error while otherwise trying to connect is worth flagging.
  if (registrationError) {
    return {
      level: 'degraded',
      labelKey: 'statusPanel.stateRegistrationError',
      fixKey: 'statusPanel.fixPhoneError',
    }
  }
  // Mid-handshake.
  return { level: 'degraded', labelKey: 'statusPanel.stateConnecting' }
}

export function realtimeHealth(snap: HealthInput): HealthRow {
  // connectionStatusAtom is written today only by useNetworkConnectivity, off
  // browser online/offline events — so CONNECTED / RECONNECTING / DISCONNECTED
  // are the values seen in practice. SUSPENDED is handled defensively but is
  // not currently emitted by any writer (the WebSocketService reconnection
  // lifecycle doesn't push it to the atom yet); kept for when it does.
  switch (snap.websocket.connectionStatus) {
    case 'CONNECTED':
      return { level: 'healthy', labelKey: 'statusPanel.stateConnected' }
    case 'RECONNECTING':
      return {
        level: 'degraded',
        labelKey: 'statusPanel.stateReconnecting',
        fixKey: 'statusPanel.fixRealtimeReconnecting',
      }
    case 'SUSPENDED':
      return {
        level: 'degraded',
        labelKey: 'statusPanel.stateSuspended',
        fixKey: 'statusPanel.fixRealtimeSuspended',
      }
    case 'DISCONNECTED':
      return {
        level: 'down',
        labelKey: 'statusPanel.stateDisconnected',
        fixKey: 'statusPanel.fixRealtimeDisconnected',
      }
    default:
      return { level: 'unknown', labelKey: 'statusPanel.overallUnknown' }
  }
}

export interface AppUpdatesInput {
  isInstallPermissionMissing: boolean
  isUpdateBlocked: boolean
  isUpdateAvailable: boolean
  isUpdateDownloaded: boolean
  availableVersion: string | null
  lastCheckStatus: UpdateCheckStatus
  isChecking: boolean
}

/**
 * Desktop-only: whether the app can keep itself up to date. A failed or
 * pending check stays neutral so a brief network blip never raises the
 * top-nav alert; only problems the user must act on are `degraded`.
 */
export function appUpdatesHealth(input: AppUpdatesInput): HealthRow {
  const version = input.availableVersion
  if (input.isInstallPermissionMissing) {
    return {
      level: 'degraded',
      labelKey: 'statusPanel.stateCantInstallUpdates',
      fixKey: 'updatePermissionBanner.message',
    }
  }
  if (input.isUpdateBlocked) {
    return {
      level: 'degraded',
      labelKey: 'statusPanel.stateUpdateBlocked',
      fixKey: 'updateBlockedBanner.message',
    }
  }
  if (input.isUpdateDownloaded) {
    return version
      ? {
          level: 'healthy',
          labelKey: 'statusPanel.stateUpdateReadyVersion',
          labelValues: { version },
        }
      : { level: 'healthy', labelKey: 'statusPanel.stateUpdateReady' }
  }
  if (input.isUpdateAvailable) {
    return version
      ? {
          level: 'healthy',
          labelKey: 'statusPanel.stateUpdateDownloadingVersion',
          labelValues: { version },
        }
      : { level: 'healthy', labelKey: 'statusPanel.stateUpdateDownloading' }
  }
  if (input.isChecking) {
    return { level: 'idle', labelKey: 'statusPanel.stateCheckingForUpdates' }
  }
  if (input.lastCheckStatus === 'failed') {
    return { level: 'unknown', labelKey: 'statusPanel.stateUpdateCheckFailed' }
  }
  // Main can drop a check without emitting anything (channel switcher open,
  // unpackaged build), so "pending" alone doesn't mean a check is running.
  if (input.lastCheckStatus === 'pending') {
    return { level: 'idle', labelKey: 'statusPanel.stateNotCheckedYet' }
  }
  return { level: 'healthy', labelKey: 'statusPanel.labelUpToDate' }
}

export function worstLevel(levels: HealthLevel[]): HealthLevel {
  return levels.reduce<HealthLevel>(
    (worst, level) => (LEVEL_RANK[level] > LEVEL_RANK[worst] ? level : worst),
    'healthy',
  )
}

export function overallHealth(rows: HealthRow[]): HealthLevel {
  return worstLevel(rows.map((row) => row.level))
}
